import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ContactHero from './components/ContactHero';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import { BookingCalendar } from '../../components/feature/BookingCalendar';

const CONTACT_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ContactPage',
      '@id': `${import.meta.env.VITE_SITE_URL || 'https://orpminvestment.com'}/contact#webpage`,
      url: `${import.meta.env.VITE_SITE_URL || 'https://orpminvestment.com'}/contact`,
      name: 'Contacto | ORPM Investment LLC – Purificación de Agua y Ventanas en Georgia',
      description: 'Contáctanos para una cotización gratuita de purificación de agua o instalación de ventanas en Georgia. Llama al +1 (561) 512-2820 o completa el formulario.',
      inLanguage: 'es-US',
      isPartOf: {
        '@type': 'WebSite',
        url: import.meta.env.VITE_SITE_URL || 'https://orpminvestment.com',
        name: 'ORPM Investment LLC',
      },
    },
    {
      '@type': 'LocalBusiness',
      name: 'ORPM Investment LLC',
      telephone: '+15615122820',
      email: 'info@orpminvestment.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '3202 Lealand Pl Ln',
        addressLocality: 'Lawrenceville',
        addressRegion: 'GA',
        postalCode: '30044',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 33.9565,
        longitude: -83.9879,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday'],
          opens: '09:00',
          closes: '14:00',
        },
      ],
    },
  ],
};

const TIMESLOTS_API =
  'https://readdy.ai/api/public/calendar/timeslots/539a8aa7-64f2-458e-98f1-a4d176d83ad2.8833a1d7d6f8364b5cea5601de4c53914d47123fc9be6f883b292922948d6406';
const APPOINTMENTS_API =
  'https://readdy.ai/api/public/calendar/appointments/539a8aa7-64f2-458e-98f1-a4d176d83ad2.8833a1d7d6f8364b5cea5601de4c53914d47123fc9be6f883b292922948d6406';

export default function ContactPage() {
  const { t } = useTranslation();

  useEffect(() => {
    const id = 'schema-contact';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(CONTACT_SCHEMA);
    document.title = 'Contacto | ORPM Investment LLC – Georgia';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Contáctanos para una cotización gratuita de purificación de agua o instalación de ventanas en Georgia. Llama al +1 (561) 512-2820 o completa el formulario.');
    return () => {
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  const bookingTexts = {
    stepDateTitle: t('booking_step_date'),
    stepTimeTitle: t('booking_step_time'),
    stepFormTitle: t('booking_step_form'),
    prevMonth: t('booking_prev_month'),
    nextMonth: t('booking_next_month'),
    noSlots: t('booking_no_slots'),
    labelName: t('booking_label_name'),
    labelPhone: t('booking_label_phone'),
    labelNotes: t('booking_label_notes'),
    phName: t('booking_ph_name'),
    phPhone: t('booking_ph_phone'),
    phNotes: t('booking_ph_notes'),
    selectedDate: t('booking_selected_date'),
    selectedTime: t('booking_selected_time'),
    modifyTime: t('booking_modify_time'),
    submitBtn: t('booking_submit'),
    submitting: t('booking_submitting'),
    successTitle: t('booking_success_title'),
    successDesc: t('booking_success_desc'),
    errorMsg: t('booking_error'),
    backBtn: t('booking_back'),
    loadingSlots: t('booking_loading'),
    required: t('booking_required'),
  };

  return (
    <>
      <Navbar />
      <main>
        <ContactHero />

        {/* ── Booking Calendar Section ─────────────────────────────────────── */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            {/* Section header */}
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-sky-600 bg-sky-50 border border-sky-100 rounded-full px-4 py-1.5 mb-3">
                {t('booking_section_tag')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
                {t('booking_section_title')}
              </h2>
              <p className="text-slate-500 text-base max-w-xl mx-auto leading-relaxed">
                {t('booking_section_desc')}
              </p>
            </div>

            {/* Calendar centered */}
            <div className="max-w-md mx-auto">
              <BookingCalendar
                timeslotsApi={TIMESLOTS_API}
                appointmentsApi={APPOINTMENTS_API}
                texts={bookingTexts}
              />
            </div>
          </div>
        </section>

        {/* ── Contact Form + Info ──────────────────────────────────────────── */}
        <section className="py-16 bg-[#F0F4F8]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
              <div className="lg:col-span-3">
                <ContactForm />
              </div>
              <div className="lg:col-span-2">
                <ContactInfo />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
